import { useMemo, useState, type CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApiQuery, useApiMutation } from '../use-api.js';
import { RequireRole } from '../components/RequireRole.js';
import {
  fmtDate,
  formatRate,
  formatActedOn,
  isCleanQuarter,
  dataQualityEntries,
  structuralEntries,
  legacyEntries,
  selectReview,
  signatureStaleFor,
  safeHttpUrl,
  allReviewsLegacy,
  manifestAbsentDisclosure,
  orgsSubsetDisclosure,
  buildQuarterlyExportSections,
  renderMarkdown,
  renderConfluenceStorage,
  type QuarterlyDetailResponse,
  type QuarterlyReviewRow,
  type QuarterlySampleRow,
  type QuarterlySignoffRow,
  type QuarterlyNoteRow,
} from './quarterly-export.js';


const sectionStyle: CSSProperties = {
  background: 'var(--loop-neutral-0)',
  border: '1px solid var(--loop-neutral-200)',
  borderRadius: 'var(--loop-radius-sm)',
  padding: 'var(--loop-space-300)',
  marginBottom: 16,
};

const h3Style: CSSProperties = {
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: 'var(--loop-neutral-1100)',
  margin: '0 0 12px',
};

const tableStyle: CSSProperties = { width: '100%', fontSize: '0.8125rem', borderCollapse: 'collapse' };
const thStyle: CSSProperties = { textAlign: 'left', padding: '4px 8px', color: 'var(--loop-neutral-700)', borderBottom: '1px solid var(--loop-neutral-200)' };
const tdStyle: CSSProperties = { padding: '4px 8px', borderBottom: '1px solid var(--loop-neutral-100)' };

const badgeSupersededStyle: CSSProperties = {
  marginLeft: 8,
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: 'var(--loop-yellow-800)',
  background: 'var(--loop-yellow-100)',
  borderRadius: 'var(--loop-radius-xs)',
  padding: '1px 6px',
};

const bannerStyle: CSSProperties = {
  background: 'var(--loop-red-100)',
  border: '1px solid var(--loop-red-600)',
  color: 'var(--loop-red-700)',
  borderRadius: 'var(--loop-radius-sm)',
  padding: 'var(--loop-space-200)',
  marginBottom: 16,
};

const reasonStyle: CSSProperties = { fontSize: '0.75rem', color: 'var(--loop-red-700)', margin: '4px 0 0' };

const supersededBannerStyle: CSSProperties = {
  background: 'var(--loop-yellow-100)',
  border: '1px solid var(--loop-yellow-800)',
  color: 'var(--loop-yellow-800)',
  borderRadius: 'var(--loop-radius-sm)',
  padding: 'var(--loop-space-200)',
  marginBottom: 16,
};

function bandStyle(band: 'clean' | 'dirty' | 'legacy'): CSSProperties {
  if (band === 'clean') {
    return { background: 'var(--loop-green-100)', border: '1px solid var(--loop-green-700)', color: 'var(--loop-green-700)', borderRadius: 'var(--loop-radius-sm)', padding: 'var(--loop-space-200)' };
  }
  if (band === 'legacy') {
    return { background: 'var(--loop-yellow-100)', border: '1px solid var(--loop-yellow-800)', color: 'var(--loop-yellow-800)', borderRadius: 'var(--loop-radius-sm)', padding: 'var(--loop-space-200)' };
  }
  return { background: 'var(--loop-yellow-100)', border: '1px solid var(--loop-yellow-800)', color: 'var(--loop-yellow-800)', borderRadius: 'var(--loop-radius-sm)', padding: 'var(--loop-space-200)' };
}

// The completeness band. "Clean quarter" styling keys ONLY on the count of
// data_quality entries — structural entries are unconditional disclosures
// about the artifact's own shape, so completeness.length is never a valid
// clean-quarter test. A legacy-categorised row never gets clean styling,
// regardless of count. Always rendered (never conditioned on the caller
// having a review row) — an org known from the manifest but with no
// REVIEW# row yet is exactly the moment completeness is LEAST assessable,
// which is the wrong moment for the unconditional disclosure area to go
// missing entirely.
function CompletenessBand({ review }: { review: QuarterlyReviewRow | null }) {
  if (!review) {
    return (
      <div style={{ marginBottom: 16 }}>
        <div data-testid="completeness-no-review" style={bandStyle('dirty')}>
          <p style={{ margin: 0, fontWeight: 600 }}>No review data for this org — completeness cannot be assessed.</p>
        </div>
      </div>
    );
  }
  const clean = isCleanQuarter(review);
  const dataQuality = dataQualityEntries(review.completeness);
  const structural = structuralEntries(review.completeness);
  const legacy = legacyEntries(review.completeness);
  const band: 'clean' | 'dirty' | 'legacy' = review.completeness_legacy ? 'legacy' : clean ? 'clean' : 'dirty';

  return (
    <div style={{ marginBottom: 16 }}>
      <div data-testid={`completeness-${band}`} style={bandStyle(band)}>
        {review.completeness_legacy && (
          <>
            <p style={{ margin: 0, fontWeight: 600 }}>
              This quarter predates completeness categorisation — regenerate it.
            </p>
            {legacy.length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                {legacy.map((e, i) => <li key={i}>{e.text}</li>)}
              </ul>
            )}
          </>
        )}
        {!review.completeness_legacy && clean && (
          <p style={{ margin: 0, fontWeight: 600 }}>Clean quarter — no data-quality issues reported.</p>
        )}
        {!review.completeness_legacy && !clean && (
          <>
            <p style={{ margin: '0 0 8px', fontWeight: 600 }}>Data-quality notes</p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {dataQuality.map((e, i) => <li key={i}>{e.text}</li>)}
            </ul>
          </>
        )}
      </div>
      {/* Structural entries are ALWAYS present (unconditional disclosures the
          aggregator appends every run) — a separate, quieter block, clearly
          labelled as describing the artifact's own shape rather than this
          quarter's data. Never folded into the clean/dirty test above. */}
      <div data-testid="completeness-structural" style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--loop-neutral-600)' }}>
        <p style={{ margin: '0 0 4px' }}>
          About this report — describes the artifact&apos;s own shape, not this quarter&apos;s data:
        </p>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {structural.map((e, i) => <li key={i}>{e.text}</li>)}
        </ul>
      </div>
    </div>
  );
}

function RevertRatesSection({ review }: { review: QuarterlyReviewRow | null }) {
  return (
    <section style={sectionStyle}>
      <h3 style={h3Style}>Revert rates — AI-approved vs. human-approved</h3>
      {review ? (
        <table style={tableStyle}>
          <thead>
            <tr>{['Arm', 'Merged', 'Reverted', 'Rate'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>AI-approved</td>
              <td style={tdStyle}>{review.revert_rates.ai_approved.merged}</td>
              <td style={tdStyle}>{review.revert_rates.ai_approved.reverted}</td>
              <td style={tdStyle}>{formatRate(review.revert_rates.ai_approved.rate)}</td>
            </tr>
            <tr>
              <td style={tdStyle}>Human-approved</td>
              <td style={tdStyle}>{review.revert_rates.human_approved.merged}</td>
              <td style={tdStyle}>{review.revert_rates.human_approved.reverted}</td>
              <td style={tdStyle}>{formatRate(review.revert_rates.human_approved.rate)}</td>
            </tr>
          </tbody>
        </table>
      ) : <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>No review data for this org.</p>}
    </section>
  );
}

// `!review` is checked FIRST. `review?.incidents ?? []` turned a null review
// row into an empty array, which rendered "No incidents linked this quarter."
// — an affirmative clean claim with no data behind it. The sibling sections
// (RevertRatesSection, OverrideSection) correctly say "No review data for this
// org." in the same situation; this one now matches them. Reachable for any
// org listed in the manifest whose REVIEW# row has not been written yet.
function IncidentsSection({ review }: { review: QuarterlyReviewRow | null }) {
  const incidents = review?.incidents ?? [];
  return (
    <section style={sectionStyle}>
      <h3 style={h3Style}>Production incidents linked to AI-approved PRs</h3>
      {!review ? (
        <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>No review data for this org.</p>
      ) : incidents.length === 0 ? (
        <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>No incidents linked this quarter.</p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.875rem' }}>
          {incidents.map((inc, i) => {
            const title = typeof inc['title'] === 'string' ? inc['title'] : 'Untitled incident';
            const severity = typeof inc['severity'] === 'string' ? inc['severity'] : 'unknown severity';
            const occurredAt = typeof inc['occurred_at'] === 'string' ? inc['occurred_at'] : 'unknown time';
            // Scheme-allowlisted, not merely type-checked: this value goes
            // straight into an href. Incidents are internally ingested so
            // this is not a live vector, but the export path already
            // neutralises the same field and this one did not. A rejected
            // URL degrades to plain text — the title still renders.
            const sourceUrl = safeHttpUrl(typeof inc['source_url'] === 'string' ? inc['source_url'] : null);
            return (
              <li key={i}>
                {sourceUrl
                  ? <a href={sourceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--loop-blue-600)' }}>{title}</a>
                  : title}
                {' '}({severity}) — {occurredAt}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function OverrideSection({ review }: { review: QuarterlyReviewRow | null }) {
  return (
    <section style={sectionStyle}>
      <h3 style={h3Style}>Engineer override usage</h3>
      {review ? (
        <table style={tableStyle}>
          <thead>
            <tr>{['Invocations', 'PRs reviewed', 'Rate'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>{review.override_usage.invocations}</td>
              <td style={tdStyle}>{review.override_usage.prs_reviewed}</td>
              <td style={tdStyle}>{formatRate(review.override_usage.rate)}</td>
            </tr>
          </tbody>
        </table>
      ) : <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>No review data for this org.</p>}
    </section>
  );
}

// Per-reviewer drift sample. `available: 0` renders the honest reason —
// since D3 (2026-08-28) that reason is "this reviewer posted nothing", not
// the old "no substantive findings" — rather than "no data" or a bare zero.
// `body_excerpt` is the STORED
// snapshot — never re-fetched — with the live URL alongside as a
// convenience. `acted_on: null` renders "Unknown". A superseded row is
// marked, not hidden.
function DriftSection({ samples }: { samples: QuarterlySampleRow[] }) {
  return (
    <section style={sectionStyle}>
      <h3 style={h3Style}>Drift sample — per reviewer, with acted-on state</h3>
      {samples.length === 0 ? (
        <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>No drift samples recorded.</p>
      ) : samples.map(s => (
        // Keyed on `sk`, NOT `reviewer`. `samplesForOrg` filters by org only,
        // and a superseded row and a current row can both exist for the same
        // (org, reviewer) — superseded rows are deliberately kept and marked
        // rather than hidden, so this is reachable BY DESIGN. Two elements
        // sharing a key make React reconcile them as one, and the wrong row's
        // body_excerpt, acted-on state or availability count renders. The sort
        // key uniquely identifies every row.
        <div key={s.sk} style={{ marginBottom: 16 }}>
          <p style={{ fontWeight: 600, fontSize: '0.875rem', margin: '0 0 4px' }}>
            {s.reviewer}
            {s.superseded && <span style={badgeSupersededStyle}>Superseded</span>}
          </p>
          <p style={{ fontSize: '0.8rem', color: 'var(--loop-neutral-700)', margin: '0 0 8px' }}>
            Requested {s.requested}, available {s.available}
          </p>
          {s.available === 0 ? (
            // D3 (2026-08-28): the sample now draws on every comment a
            // reviewer posted, not only those from reviewers that marked a
            // finding substantive — so available: 0 means silence, not
            // filtered-out. The old advisory-only copy would now be false.
            <p style={{ fontSize: '0.8125rem' }}>
              This reviewer posted no comments at all this quarter. The sample draws on every
              comment a reviewer posted, substantive or advisory, so this is an absence of
              output — not a data gap.
            </p>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>{['PR', 'Comment (stored snapshot)', 'Acted on', 'Link'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {s.sampled.map(c => {
                  // Same scheme allowlist as the incident source_url above.
                  // Not currently exploitable — c.url is built server-side
                  // from a literal https:// prefix with encodeURIComponent'd
                  // org/repo and number-guarded pr/commentId — but this was
                  // one of two href sites in this file and only the other one
                  // had the check. A rejected URL degrades to plain text, as
                  // the incident branch does, since the value is evidence.
                  const link = safeHttpUrl(c.url);
                  return (
                    <tr key={c.comment_id}>
                      <td style={tdStyle}>#{c.pr}</td>
                      <td style={tdStyle}>{c.body_excerpt}</td>
                      <td style={tdStyle}>{formatActedOn(c.acted_on)}</td>
                      <td style={tdStyle}>
                        {link
                          ? <a href={link} target="_blank" rel="noreferrer" style={{ color: 'var(--loop-blue-600)' }}>View comment ↗</a>
                          : c.url}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </section>
  );
}

function BaselinePolicySection({ review }: { review: QuarterlyReviewRow | null }) {
  const policyVersions = review?.policy_versions ?? [];
  const baselineChanges = review?.baseline_changes ?? [];
  return (
    <section style={sectionStyle}>
      <h3 style={h3Style}>Baseline and policy changes during the period</h3>
      <p style={{ fontWeight: 528, fontSize: '0.8125rem', margin: '0 0 4px' }}>Policy versions</p>
      {policyVersions.length === 0 ? (
        <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>No policy version changes recorded this period.</p>
      ) : (
        <ul style={{ margin: '0 0 12px', paddingLeft: 20, fontSize: '0.875rem' }}>
          {policyVersions.map((v, i) => (
            <li key={i}>
              {String(v['version'] ?? 'unknown version')} — {String(v['pushed_at'] ?? 'unknown date')}
              {v['commit_message'] ? `: ${String(v['commit_message'])}` : ''}
            </li>
          ))}
        </ul>
      )}
      <p style={{ fontWeight: 528, fontSize: '0.8125rem', margin: '0 0 4px' }}>Baseline changes</p>
      {baselineChanges.length === 0 ? (
        <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>No baseline changes recorded this period.</p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.875rem' }}>
          {baselineChanges.map((b, i) => (
            <li key={i}>{String(b['repo'] ?? 'unknown repo')} — re-baselined {String(b['computed_at'] ?? 'unknown date')}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

// The UI gate here is cosmetic — RequireRole hides the control from a
// non-superAdmin viewer, but the actual control is the server's superAdmin
// gate on POST /api/quarterly/{quarter}/signoff (Task 5). A missing
// manifest disables the button with the same reason Task 5's 409 states,
// so nobody is invited to click something that cannot succeed.
function SignoffPanel({
  quarter, org, manifestPresent, current, history, signatureStale, currentGeneratedAt,
}: {
  quarter: string;
  org: string;
  manifestPresent: boolean;
  current: QuarterlySignoffRow | null;
  history: QuarterlySignoffRow[];
  /** The sign-off's `report_generated_at` does not match the report shown on
   *  this page — the quarter was regenerated after it was signed. */
  signatureStale: boolean;
  /** The currently-displayed report's `generated_at`, for the banner's
   *  side-by-side. null when there is no REVIEW# row for this org at all,
   *  which is itself a stale state (nothing to bind to). */
  currentGeneratedAt: string | null;
}) {
  const mutation = useApiMutation(`/api/quarterly/${quarter}/signoff`);
  const [attestation, setAttestation] = useState('');
  const errMessage = mutation.error instanceof Error ? mutation.error.message : null;

  return (
    <section style={sectionStyle}>
      <h3 style={h3Style}>Sign-off</h3>
      {current ? (
        // Deliberately NOT green when the signature is stale: green beside
        // "Signed by X" is the whole misstatement. The sign-off is still
        // reported in full (it is real evidence of a real past attestation) —
        // what changes is that the page stops claiming it covers what is on
        // screen.
        <p style={{ fontSize: '0.875rem', color: signatureStale ? 'var(--loop-yellow-800)' : 'var(--loop-green-700)' }}>
          Signed by {current.signed_by} ({current.signed_by_role}) at {fmtDate(current.signed_at)} — revision {current.revision}.
        </p>
      ) : (
        <p style={{ fontSize: '0.875rem', color: 'var(--loop-neutral-700)' }}>Not signed off for {org}.</p>
      )}
      {/* Not inside the print-hidden block: a stale signature is evidence
          about the evidence, and a printed report that shows the sign-off
          without it would be the same false claim on paper. */}
      {current && signatureStale && (
        <div role="alert" data-testid="signature-stale-banner" style={supersededBannerStyle}>
          <strong>This sign-off does not cover the report shown below.</strong>
          <p style={{ margin: '4px 0 0' }}>
            The signature binds to the report generated {fmtDate(current.report_generated_at)}, but this
            page shows the generation from {currentGeneratedAt ? fmtDate(currentGeneratedAt) : 'an unknown time (no report row on record)'}.
            The quarter was regenerated after it was signed, so the figures above have not been
            attested to. Review and re-sign.
          </p>
        </div>
      )}
      <RequireRole min="superAdmin">
        {/* Write controls only — the sign-off STATE above (signed/not signed,
            history below) is evidence and always prints; the form itself is
            chrome for producing new evidence, not the evidence. */}
        <div className="quarterly-print-hide">
          <textarea
            value={attestation}
            onChange={e => setAttestation(e.target.value)}
            placeholder="Attestation — what was reviewed and approved"
            style={{ width: '100%', minHeight: 60, boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '0.875rem', marginTop: 8 }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <button
              className="btn-sm"
              disabled={!manifestPresent || mutation.isPending}
              onClick={() => { if (attestation.trim()) mutation.mutate({ org, attestation }); }}
              style={{ background: 'var(--loop-brand-600)', color: 'var(--loop-neutral-0)', border: 'none', borderRadius: 'var(--loop-radius-sm)', cursor: (!manifestPresent || mutation.isPending) ? 'not-allowed' : 'pointer', opacity: (!manifestPresent || mutation.isPending) ? 0.6 : 1 }}
            >
              {mutation.isPending ? 'Signing off…' : 'Sign off'}
            </button>
          </div>
          {!manifestPresent && (
            <p style={reasonStyle}>Sign-off disabled: generation did not complete for this quarter.</p>
          )}
          {errMessage && <p className="error">{errMessage}</p>}
        </div>
      </RequireRole>
      {history.length > 0 && (
        <details style={{ marginTop: 12, fontSize: '0.8125rem', color: 'var(--loop-neutral-700)' }}>
          <summary>History ({history.length})</summary>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {history.map(h => (
              <li key={h.sk}>{fmtDate(h.signed_at)} — {h.signed_by} (revision {h.revision})</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

// UI gate is cosmetic, same as sign-off — the server's triager gate on
// POST /api/quarterly/{quarter}/notes (Task 6) is the control.
function NotesPanel({ quarter, org, notes }: { quarter: string; org: string; notes: QuarterlyNoteRow[] }) {
  const mutation = useApiMutation(`/api/quarterly/${quarter}/notes`);
  const [body, setBody] = useState('');
  const errMessage = mutation.error instanceof Error ? mutation.error.message : null;

  return (
    <section style={sectionStyle}>
      <h3 style={h3Style}>Review notes</h3>
      {notes.length === 0 ? (
        <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>No notes recorded.</p>
      ) : (
        <ul style={{ margin: '0 0 8px', paddingLeft: 20, fontSize: '0.875rem' }}>
          {notes.map(n => <li key={n.sk}>{fmtDate(n.created_at)} — {n.author}: {n.body}</li>)}
        </ul>
      )}
      <RequireRole min="triager">
        <div className="quarterly-print-hide">
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="e.g. sampled 30 comments for pr-reviewer-security; two looked like blind spots"
            style={{ width: '100%', minHeight: 48, boxSizing: 'border-box', fontFamily: 'inherit', fontSize: '0.875rem' }}
          />
          <button
            className="btn-sm"
            disabled={mutation.isPending}
            onClick={() => { if (body.trim()) { mutation.mutate({ org, body }); setBody(''); } }}
            style={{ marginTop: 8, background: 'var(--loop-brand-600)', color: 'var(--loop-neutral-0)', border: 'none', borderRadius: 'var(--loop-radius-sm)', cursor: mutation.isPending ? 'not-allowed' : 'pointer' }}
          >
            {mutation.isPending ? 'Adding…' : 'Add note'}
          </button>
          {errMessage && <p className="error">{errMessage}</p>}
        </div>
      </RequireRole>
    </section>
  );
}

// A pure client-side render of the same serialiser tested in
// quarterly-export.test.ts — offered as copyable text, never a download:
// <a download> is inert inside a sandboxed viewer, and this surface may be
// reached through one. Hidden entirely on print — it duplicates the report
// already printed above, not additional evidence.
function ExportPanel({ detail, org }: { detail: QuarterlyDetailResponse; org: string }) {
  const [format, setFormat] = useState<'markdown' | 'confluence'>('markdown');
  const sections = useMemo(() => buildQuarterlyExportSections(detail, org), [detail, org]);
  const markdown = useMemo(() => renderMarkdown(sections), [sections]);
  const confluence = useMemo(() => renderConfluenceStorage(sections), [sections]);
  const text = format === 'markdown' ? markdown : confluence;

  return (
    <section style={sectionStyle} className="quarterly-print-hide">
      <h3 style={h3Style}>Export</h3>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <button className="btn-sm" aria-pressed={format === 'markdown'} onClick={() => setFormat('markdown')}>Markdown</button>
        <button className="btn-sm" aria-pressed={format === 'confluence'} onClick={() => setFormat('confluence')}>Confluence storage format</button>
        <button
          className="btn-sm"
          onClick={() => { navigator.clipboard?.writeText(text).catch(() => {}); }}
        >
          Copy
        </button>
      </div>
      <textarea readOnly value={text} rows={10} style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'monospace', fontSize: '0.75rem' }} />
    </section>
  );
}

export function QuarterlyDetail() {
  const { quarter } = useParams<{ quarter: string }>();
  const { data, isLoading, error } = useApiQuery<QuarterlyDetailResponse>(
    ['quarterly-detail', quarter],
    `/api/quarterly/${quarter}`,
  );
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null);

  // SELECTABLE orgs are the manifest's, not "every org with a REVIEW# row".
  // A re-run that covers fewer orgs overwrites the rows it covers and leaves
  // the rest in place, so the partition accumulates superseded rows for orgs
  // outside the population — and offering those for selection put a sign-off
  // button under them. On 2026-08-28 that made a smoke-test fixture ("test",
  // never a GitHub org) and a non-production org selectable on 2026-Q2. The
  // API rejects such a signing now, but a control surface should not offer an
  // action it knows will be refused.
  const orgList = useMemo(() => {
    if (!data) return [];
    const manifestOrgs = data.manifest?.orgs.map(o => o.org) ?? [];
    // No manifest at all is the known-partial case the banner below already
    // explains; fall back to observed rows so the page still renders its data
    // rather than going blank on the quarter that most needs reading.
    if (manifestOrgs.length === 0) return [...new Set(data.reviews.map(r => r.org))].sort();
    return [...new Set(manifestOrgs)].sort();
  }, [data]);

  // Surfaced, never selectable — dropping them silently would be the same
  // defect one level down: the reader could not tell "this quarter covers
  // five orgs" from "this quarter has rows the manifest disowns".
  const supersededOrgs = useMemo(() => {
    if (!data?.manifest) return [];
    const inManifest = new Set(data.manifest.orgs.map(o => o.org));
    return [...new Set(data.reviews.map(r => r.org).filter(o => !inManifest.has(o)))].sort();
  }, [data]);

  // Clamped to orgList, not just defaulted from it. `selectedOrg` is component
  // state that survives a data change — navigating from one quarter to another
  // re-renders this same component with a different manifest — so a selection
  // made while an org was in scope could persist into a quarter whose manifest
  // disowns it, re-enabling the report and the sign-off panel for an org the
  // selector itself no longer offers. Making the selector manifest-driven is
  // not enough on its own if the value it feeds is never re-validated.
  const org = selectedOrg !== null && orgList.includes(selectedOrg)
    ? selectedOrg
    : orgList[0] ?? null;

  // selectReview is the ONE place a review row is looked up for an org —
  // shared with the export serialiser — so the on-screen render and the
  // exported document can't independently invent two "which row is
  // current" rules that disagree (there is only ever at most one REVIEW#
  // row per org to begin with; see selectReview's own comment).
  const review = useMemo(() => (data && org ? selectReview(data.reviews, org) : null), [data, org]);

  const samplesForOrg = useMemo(
    () => (data ? [...data.samples].filter(s => s.org === org).sort((a, b) => a.reviewer.localeCompare(b.reviewer)) : []),
    [data, org],
  );

  const currentSignoff = data?.signoffs.current.find(s => s.org === org) ?? null;
  const historySignoffs = (data?.signoffs.history ?? []).filter(s => s.org === org);
  const notesForOrg = (data?.notes ?? []).filter(n => n.org === org);
  // One rule, shared with the export serialiser — see signatureStaleFor.
  const signatureStale = data && org ? signatureStaleFor(data, org) : false;

  // The legacy 2026-Q2 row's generation DID complete; it simply predates the
  // manifest convention. Saying "generation did not complete" for it is false,
  // and it is the first thing anyone sees on this page. Softened only when
  // every review row on the quarter is legacy-shaped — a partition with any
  // tagged row is a genuinely incomplete or partially-regenerated generation
  // and keeps the hard wording. The disabled sign-off button is correct in
  // both cases (the server 409s without a manifest either way), so only the
  // explanation changes.
  //
  // Both the test and the wording now come from quarterly-export.ts, shared
  // with the export serialiser: the export was still emitting the hard
  // "this generation did not complete" line for legacy quarters the screen had
  // already softened, and the export is the surface that becomes the
  // compliance record.
  const reviewsAreLegacy = !!data && allReviewsLegacy(data);
  const manifestAbsent = manifestAbsentDisclosure(reviewsAreLegacy);
  const subsetDisclosure = orgsSubsetDisclosure(data?.quarter ?? '');

  // `quarterly-report` scopes the @media print rule that force-opens a
  // collapsed <details> (the sign-off history) to THIS page. Unscoped, it also
  // forced open unrelated <details> elements on other routes — the
  // weekly-digest page has its own — turning a printed digest into a wall of
  // expanded sections nobody asked for. See styles.css.
  return (
    <div className="quarterly-report">
      <div className="quarterly-print-hide" style={{ marginBottom: 16 }}>
        <Link to="/quarterly" style={{ color: 'var(--loop-blue-600)', fontSize: '0.875rem' }}>← Quarterly Review</Link>
      </div>

      {isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
      {error !== null && <p className="error">Error: {String(error)}</p>}

      {data && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 8, flexWrap: 'wrap' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>
              Quarterly Review — {data.quarter}
            </h2>
            {orgList.length > 1 && (
              <select
                className="quarterly-print-hide"
                value={org ?? ''}
                onChange={e => setSelectedOrg(e.target.value)}
                aria-label="Organisation"
              >
                {orgList.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--loop-neutral-700)', margin: '0 0 4px' }}>
            Period: {data.period_start} – {data.period_end}
          </p>
          {supersededOrgs.length > 0 && (
            <p style={{ fontSize: '0.8125rem', color: 'var(--loop-neutral-700)', margin: '0 0 4px' }}>
              This quarter&apos;s partition also holds review rows for{' '}
              <strong>{supersededOrgs.join(', ')}</strong>, left by an earlier generation that
              covered a different set of organisations. They are not part of this quarter&apos;s
              population, are not shown above, and cannot be signed off.
            </p>
          )}
          <p style={{ fontSize: '0.875rem', color: 'var(--loop-neutral-700)', margin: '0 0 16px' }}>
            Generated: {data.manifest ? fmtDate(data.manifest.generated_at) : 'not available'}
          </p>

          {!data.manifest_present && (
            reviewsAreLegacy ? (
              <div role="alert" data-testid="manifest-legacy-banner" style={supersededBannerStyle}>
                <strong>{manifestAbsent.headline}</strong>
                <p style={{ margin: '4px 0 0' }}>{manifestAbsent.detail}</p>
              </div>
            ) : (
              <div role="alert" data-testid="manifest-missing-banner" style={bannerStyle}>
                <strong>{manifestAbsent.headline}</strong>
                <p style={{ margin: '4px 0 0' }}>{manifestAbsent.detail}</p>
              </div>
            )
          )}

          {/* A manifest written by an explicit-subset run lists only the orgs
              that run was asked for, so `manifest.orgs` is NOT this quarter's
              contents. The gauge already refuses to count such a quarter
              signed; without saying so here, the portal and the gauge would
              disagree with nothing on screen explaining which is right.
              Wording shared with the export — see orgsSubsetDisclosure, which
              also explains why the remedy names the payload rather than saying
              "regenerate over every org" (an explicit `orgs` list re-stamps
              the flag even when it names every org, so the old instruction
              sent the operator in a circle). */}
          {data.manifest?.orgs_subset === true && (
            <div data-testid="manifest-subset-banner" style={supersededBannerStyle}>
              <strong>{subsetDisclosure.headline}</strong>
              <p style={{ margin: '4px 0 0' }}>{subsetDisclosure.detail}</p>
            </div>
          )}

          {review?.superseded && (
            <div data-testid="review-superseded-banner" style={supersededBannerStyle}>
              <strong>Superseded — generated {fmtDate(review.generated_at)}, older than this quarter&apos;s manifest.</strong>
              <p style={{ margin: '4px 0 0' }}>
                This is the only REVIEW# row on record for this org this quarter, but it predates the
                current manifest. Every figure below is from that older generation — shown, not hidden.
              </p>
            </div>
          )}
          {org && <CompletenessBand review={review} />}

          {org && (
            <>
              <RevertRatesSection review={review} />
              <IncidentsSection review={review} />
              <OverrideSection review={review} />
              <DriftSection samples={samplesForOrg} />
              <BaselinePolicySection review={review} />

              <SignoffPanel
                quarter={data.quarter}
                org={org}
                manifestPresent={data.manifest_present}
                current={currentSignoff}
                history={historySignoffs}
                signatureStale={signatureStale}
                currentGeneratedAt={review?.generated_at ?? null}
              />
              <NotesPanel quarter={data.quarter} org={org} notes={notesForOrg} />
              <ExportPanel detail={data} org={org} />
            </>
          )}
        </>
      )}
    </div>
  );
}
